import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {DataEntity, OctopusConnectService} from 'octopus-connect';
import {ActivitiesService} from '@modules/activities/core/activities.service';
import {
    SearchFilterDefinition,
    SearchFilterQueryAdapter,
    SearchFilterSelection,
    SearchFilterSelectionValue,
    SearchFilterValue,
    isSearchFilterSelectionArray,
} from 'shared/search-engine';
import {
    buildMathiaFilterDefinitions,
    MATHIA_SEARCH_FILTER_CONFIG,
    MATHIA_PARCOURS_SEARCH_FILTER_CONFIG,
    MathiaFacetScope,
    MathiaSearchFilterConfig,
    MathiaSearchSelection,
} from './mathia-search-filter-config';

@Injectable({providedIn: 'root'})
export class MathiaSearchDataService implements SearchFilterQueryAdapter {
    public readonly urlParamKeys = [
        'educationalLevel',
        'level',
        'concepts',
        'thematics',
        'skills',
        'chapters',
        'bookmarks',
    ];

    private readonly octopus = inject(OctopusConnectService);
    private readonly activitiesService = inject(ActivitiesService);
    /**
     * The initial call intentionally has no filters. It preloads every facet;
     * subsequent calls receive the current selection so the API can recalculate
     * the available downstream facets.
     */
    public getFilterDefinitions(
        selection: SearchFilterSelection<SearchFilterValue> = {},
        config: readonly MathiaSearchFilterConfig[] = MATHIA_SEARCH_FILTER_CONFIG,
        facetScope: MathiaFacetScope = 'activity',
    ): Observable<SearchFilterDefinition<SearchFilterValue>[]> {
        return this.loadFacets(selection, config, facetScope).pipe(
            map(facets => buildMathiaFilterDefinitions(facets, config)),
        );
    }

    public toQueryFilters(selection: SearchFilterSelection<SearchFilterValue>): Record<string, SearchFilterValue> {
        const entries = Object.entries(selection).flatMap(([key, value]) => {
            const config = MATHIA_PARCOURS_SEARCH_FILTER_CONFIG.find(item => item.key === key);
            return this.toSerializedEntry(config?.queryParam ?? key, value, config?.omitWhenFalse === true);
        });

        return Object.fromEntries(entries);
    }

    public toUrlParams(selection: SearchFilterSelection<SearchFilterValue>): Record<string, SearchFilterValue> {
        const entries = Object.entries(selection).flatMap(([key, value]) => {
            const config = MATHIA_PARCOURS_SEARCH_FILTER_CONFIG.find(item => item.key === key);
            const queryParam = key === 'level' ? 'educationalLevel' : (config?.queryParam ?? key);
            return this.toSerializedEntry(queryParam, value, config?.omitWhenFalse === true);
        });

        return Object.fromEntries(entries);
    }

    public searchActivities(selection: MathiaSearchSelection): Observable<DataEntity[]> {
        return this.activitiesService.loadPaginatedActivities({
            filter: this.toQueryFilters(selection),
            page: 1,
            range: 12,
        }, 'lesson_granule_search').pipe(
            map(result => result.entities as DataEntity[]),
        );
    }

    private loadFacets(
        selection: SearchFilterSelection<SearchFilterValue>,
        config: readonly MathiaSearchFilterConfig[] = MATHIA_SEARCH_FILTER_CONFIG,
        facetScope: MathiaFacetScope = 'activity',
    ): Observable<Record<string, unknown>> {
        return this.octopus
            .loadCollection('lesson_facets', this.toFacetFilters(selection, config, facetScope))
            .pipe(
                map(collection => (collection.entities[0]?.attributes ?? {}) as Record<string, unknown>),
            );
    }

    private toFacetFilters(
        selection: SearchFilterSelection<SearchFilterValue>,
        config: readonly MathiaSearchFilterConfig[],
        facetScope: MathiaFacetScope,
    ): Record<string, SearchFilterValue> {
        const entries: [string, SearchFilterValue][] = config
            .filter(filterConfig => !!filterConfig.facetKey)
            .flatMap(filterConfig => this.toSerializedEntry(
                filterConfig.facetKey!,
                selection[filterConfig.key],
            ));

        return Object.fromEntries([
            ['scope', facetScope],
            ...entries,
        ]);
    }

    private serializeSelectionValue(
        value: SearchFilterSelectionValue<SearchFilterValue>,
        omitWhenFalse = false,
    ): SearchFilterValue | null {
        if (value === null || value === undefined) {
            return null;
        }

        if (omitWhenFalse && value === false) {
            return null;
        }

        if (isSearchFilterSelectionArray(value)) {
            return value.length > 0 ? value.join(',') : null;
        }

        return (value as SearchFilterValue) === '' ? null : value as SearchFilterValue;
    }

    private toSerializedEntry(
        key: string,
        value: SearchFilterSelectionValue<SearchFilterValue>,
        omitWhenFalse = false,
    ): [string, SearchFilterValue][] {
        const serializedValue = this.serializeSelectionValue(value, omitWhenFalse);
        return serializedValue === null ? [] : [[key, serializedValue]];
    }
}
